import React from 'react';
import { useTranslation } from 'react-i18next';

import { Line } from 'react-chartjs-2';

import { format, parseISO } from 'date-fns';
import setColors from 'lib/utils/setColors';
import { useTheme } from '@mui/material';
import { isClockTime, formatClockTimeValue } from 'lib/utils/units';

const pointColor = (graphData, theme, scoreType = 'RISK') => {
  let colors = [];
  if (!graphData || !Array.isArray(graphData)) {
    return colors;
  }
  
  
  for (let i = 0; i < graphData.length; i++) {
    const dataPoint = graphData[i];
    const score = dataPoint?.score;
    // Ensure score is a number for proper color calculation
    const numericScore = typeof score === 'string' ? parseFloat(score) : score;
    const color = setColors(numericScore, theme, scoreType).color;
    colors.push(color);
  }
  return colors;
};

const Chart = (props) => {
  const { state, handleChooseAnswer, yAxesOptions } = props;
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const { graphData, unit, title, scoreType, unitCode } = state;

  const graphDataValues = graphData?.map((item) => item?.value?.value) || [];
  const graphDataScores = graphData?.map((item) => item?.score) || [];
  const graphDates = graphData?.map((item) => item?.date) || [];

  const options = {
    legend: {
      display: false,
    },
    onClick: (e, v) => {
      const index = v[0]?._index;
      handleChooseAnswer(graphData[index]);
    },
    ticks: {
      callback: function (value) {
        if (!value) return;
        return format(parseISO(value), 'dd.MM');
      },
    },
    scales: {
      display: false,
      xAxes: [
        {
          id: 'first-x-axis',
          offset: true,
          type: 'category',
          labels: graphDates, // labels below
          gridLines: {
            display: false,
          },
          ticks: {
            callback: function (value) {
              if (!value) return;
              return format(parseISO(value), 'dd.MM');
            },
          },
        },
      ],
      yAxes: [
        {
          type: 'linear',
          display: true,
          position: 'left',
          text: unit,
          offset: true,
          gridLines: {
            drawTicks: false,
            drawBorder: false,
          },
          ticks: {
            padding: 10,
            callback: function (value) {
              if (isClockTime(unitCode)) {
                return formatClockTimeValue(value, i18n.language);
              }
              return unit ? value + '\n' + unit : value;
            },
          },
          ...yAxesOptions,
        },
      ],
    },
    responsive: true,
  };

  const patientByAgeData = (canvas) => {
    const ctx = canvas.getContext('2d');
    const gradient = ctx.createLinearGradient(0, 0, 0, 0);

    return {
      datasets: [
        {
          xAxisID: 'first-x-axis',
          lineTension: 0,
          backgroundColor: gradient,
          label: title, // change title to graphDataValues.data.component.code
          borderColor: theme.palette.colors.gray500,
          borderWidth: 1,
          pointRadius: 3,
          pointBackgroundColor: graphData ? pointColor(graphData, theme, scoreType) : [],
          data: graphDataValues,
        },
      ],
    };
  };

  // Don't render if no data
  if (!graphData || graphData.length === 0) {
    return null;
  }

  return <Line data={patientByAgeData} options={options} />;
};

export default Chart;
